import { Button, Icon, Modal, TextAreaInput } from '@octano/global-ui';
import {
  forwardRef,
  Ref,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { TranslationsKeys } from '../../../../locales/translations';

export type SectionTeachersModalMethods = {
  open: (name: string, teachers: string[]) => void;
  close: () => void;
};

type SectionTeachersModalProps = {
  onClose?: () => void;
};

const SectionTeachersModal = (
  { onClose }: SectionTeachersModalProps,
  ref: Ref<SectionTeachersModalMethods>,
) => {
  const { t } = useTranslation(TranslationsKeys.CONTINUOUS_EDUCATION_SECTIONS);

  const [sectionName, setSectionName] = useState<string>('');
  const [teacherNames, setTeacherNames] = useState<string[]>([]);

  const participantsText = useMemo(() => {
    return teacherNames.map((name) => `- ${name}`).join('\n');
  }, [teacherNames]);

  const handleOpen = useCallback((name: string, teachers: string[]) => {
    setSectionName(name);
    setTeacherNames(teachers);
  }, []);

  const handleClose = useCallback(() => {
    setTeacherNames([]);
    setSectionName('');
    !!onClose && onClose();
  }, [onClose]);

  useImperativeHandle(ref, () => ({
    open: handleOpen,
    close: handleClose,
  }));

  return (
    <Modal isOpen={!!teacherNames.length} toggle={handleClose}>
      <div className="d-flex flex-column align-items-center text-center">
        <Icon name="information" size={51} />
        <h2 className="text-dark fs-22 mt-4 mb-2 w-100 text-center">
          {t('teachersModal.title', { sectionName })}
        </h2>
        <p className="fw-18 w-100 text-center">
          {t('teachersModal.description')}
        </p>
        <div className="w-100 mb-5 mt-4">
          <TextAreaInput
            name="sectionTeachers"
            label={t('teachersModal.label')}
            value={participantsText}
            readOnly
            height={200}
            className="w-100 text-left"
          />
        </div>
        <div className="w-100">
          <Button
            className="w-100"
            type="button"
            size="md"
            fullwidth
            text={t('teachersModal.confirm')}
            onClick={handleClose}
          />
        </div>
      </div>
    </Modal>
  );
};

export default forwardRef(SectionTeachersModal);
